import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ClipboardList, Loader2, RefreshCw } from 'lucide-react';
import { useSurveys, formatDate, getStatusBadgeColor, getWindowTypeLabel } from './hooks/useSurveys';
import SurveyStats from './SurveyStats';
import SurveyCard from './SurveyCard';
import CreateSurveyDialog from './CreateSurveyDialog';
import ViewSurveyDialog from './ViewSurveyDialog';

const SurveyManagement = () => {
  const {
    surveys,
    loading,
    stats,
    statusFilter,
    setStatusFilter,
    loadSurveys,
    loadStats,
    deleteSurvey,
    updateSurveyStatus,
    createSurvey
  } = useSurveys();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [expandedSurvey, setExpandedSurvey] = useState(null);

  return (
    <div className="space-y-6" data-testid="survey-management">
      {/* Stats Cards */}
      <SurveyStats stats={stats} />

      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex items-center gap-4">
          <Label>Filter by status:</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-600">{surveys.length} survey(s)</span>
        </div>

        <div className="flex gap-2">
          <Button onClick={loadSurveys} disabled={loading} variant="outline" data-testid="refresh-surveys">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
          <CreateSurveyDialog 
            open={showCreateDialog} 
            onOpenChange={setShowCreateDialog} 
            onCreated={() => { loadSurveys(); loadStats(); }}
            createSurvey={createSurvey}
          />
        </div>
      </div>

      {/* Surveys List */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#8CB89F]" />
          <p className="text-lg text-gray-600">Loading surveys...</p>
        </div>
      ) : surveys.length === 0 ? (
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardContent className="text-center py-12">
            <ClipboardList className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg text-gray-600">No surveys found</p>
            <p className="text-sm text-gray-500 mt-2">Create your first survey to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {surveys.map((survey) => (
            <SurveyCard 
              key={survey.id} 
              survey={survey}
              expanded={expandedSurvey === survey.id}
              onToggleExpand={() => setExpandedSurvey(expandedSurvey === survey.id ? null : survey.id)}
              onDelete={() => deleteSurvey(survey.id)}
              onStatusChange={(status) => updateSurveyStatus(survey.id, status)}
              onView={() => setSelectedSurvey(survey)}
              onDownloadOrderSheet={(surveyId) => {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/surveys/${surveyId}/pdf`;
                window.open(url, '_blank');
              }}
              formatDate={formatDate}
              getStatusBadgeColor={getStatusBadgeColor}
              getWindowTypeLabel={getWindowTypeLabel}
            />
          ))}
        </div>
      )}

      {/* View Survey Dialog */}
      {selectedSurvey && (
        <ViewSurveyDialog 
          survey={selectedSurvey} 
          open={!!selectedSurvey} 
          onOpenChange={(open) => !open && setSelectedSurvey(null)}
          getWindowTypeLabel={getWindowTypeLabel}
          onSurveyUpdated={() => {
            setSelectedSurvey(null);
            loadSurveys();
            loadStats();
          }}
        />
      )}
    </div>
  );
};

export default SurveyManagement;
